import {QueryClient} from '@tanstack/react-query';
import {Request} from 'express';
import {ParsedUrlQuery} from 'querystring';
import {getEnv} from '../common/env/env';
import {tt} from '../common/getTranslations';
import {SubtabTypeVo} from '../common/shared/api/karina/vo/enums/SubtabTypeVo.enum';
import {convertQueryInputToSingleElement} from '../common/shared/utils/convertQueryInputToSingleElement';
import {GNB_MENU_UID_MAP} from '../domain/menu/constant';
import {MenuState} from '../domain/menu/type';
import {findSubtabMenuAndScreen} from '../domain/menu/util/findSubtabMenuAndScreen';
import {selectGnbMenu, setSubtabMenuMap} from '../domain/menu/util/MenuSlice';
import {addError} from '../errors/ErrorSlice';
import {thunkSerializeError} from '../errors/thunkSerializeError.function';
import {layoutFactory} from '../factory/layoutFactory';
import {MetaInfo} from '../meta/MetaInfo.interface';
import {
  setLayout,
  setLayoutIdAndScreenUidMap,
} from '../pagewebLayout/PagewebLayoutSlice';
import {createQueryClient} from '../reactQuery/createQueryClient';
import {AppStore} from '../state/store';
import {queryStaticLandingLayout} from '../staticLanding/queryStaticLandingLayout';

export async function handleMenuData({
  store,
  menu,
  menuUid,
  screenUid,
  ssrRequest,
  query,
  cookie,
}: {
  store: AppStore;
  menu: MenuState['menu'];
  menuUid: number;
  screenUid: number;
  ssrRequest?: Request;
  query: ParsedUrlQuery;
  cookie?: string;
}): Promise<{
  menuUid: number;
  screenUid: number;
  metaInfo: MetaInfo;
  layoutId?: string;
  queryClient?: QueryClient;
}> {
  const gnbMenuUidMap = GNB_MENU_UID_MAP;

  if (menu.error) {
    store.dispatch(addError({error: menu.error}));
    return {menuUid, screenUid, metaInfo: {}, layoutId: ''};
  }

  store.dispatch(setSubtabMenuMap({subtabMenuList: menu.subtabMenuList}));
  const matchedMenu = findSubtabMenuAndScreen({
    subtabMenuList: menu.subtabMenuList,
    subtab: {
      screenUid,
      menuUid,
    },
  });

  if (matchedMenu) {
    const gnbMenu = gnbMenuUidMap[matchedMenu.subtabMenu.uid];
    if (gnbMenu) {
      store.dispatch(selectGnbMenu(gnbMenu.id));
    }
  }

  const metaTitle = `${tt(gnbMenuUidMap[menuUid])} - ${matchedMenu?.screen?.title}`;
  const metaInfo: MetaInfo = {
    title: metaTitle,
    keywords: '카테고리',
    url: `${getEnv().webOrigin}/menu/${menuUid}/screen/${screenUid}`,
  };

  const selectedSubtab =
    screenUid !== undefined
      ? store.getState().menu.entities.subtabMap[screenUid]
      : undefined;

  let layoutId = undefined;
  if (selectedSubtab) {
    if (
      selectedSubtab.type === SubtabTypeVo.Landing &&
      selectedSubtab.landingType
    ) {
      const categoryUid =
        convertQueryInputToSingleElement(query.category_uid) ??
        String(selectedSubtab.categoryUid);
      const subcategoryUid = convertQueryInputToSingleElement(
        query.subcategory_uid
      );

      // categoryUid 정보가 존재하지 않는 경우는 API 요청을 할 수 없으므로, 에러를 임의로 발생시킵니다.
      if (!categoryUid) {
        store.dispatch(
          addError({
            error: thunkSerializeError(new Error('layout 없음')),
          })
        );
        return {
          menuUid,
          screenUid,
          metaInfo,
        };
      }
      const uidInfoList = [categoryUid];
      if (subcategoryUid) {
        uidInfoList.push(subcategoryUid);
      }
      try {
        const result = await queryStaticLandingLayout<any, any>({
          type: selectedSubtab.landingType,
          cookie,
          uidInfoList,
          query,
          screenUid,
          isSubtab: true,
          ssrRequest,
        });

        result.data[result.queryName].screenUid = selectedSubtab.uid;
        layoutId = result.data[result.queryName].id;
        /**
         * TODO: getServerSideProps -> getInitialProps 로 넘어오면서 기존 hydrate 시 무시되던 page:0 이 getInitialProps 이후로 덮어쓰면서 백버튼을 통해 다시 들어올 시 무조건 0 페이지만 보여주는 현상이 있습니다.
         * react-query 로 변경하면 페이지 정보도 캐싱되기 때문에 자연스럽게 해결될 것으로 보이지만
         * 당장 변경하긴 어려우니 임시로 한번 가져왔던 layout 이면 redux 에 넣지 않도록 합니다.
         */
        if (store.getState().pagewebLayout.entities.layouts[layoutId]) {
          store.dispatch(
            setLayoutIdAndScreenUidMap({
              screenUid: selectedSubtab.uid,
              layoutId,
            })
          );
        } else {
          store.dispatch(
            setLayout({
              layout: result.data[result.queryName],
              ageGrade: selectedSubtab?.ageGrade,
            })
          );
        }

        return {
          menuUid,
          screenUid,
          metaInfo,
          layoutId,
          queryClient: result.queryClient,
        };
      } catch (error) {
        store.dispatch(addError({error: thunkSerializeError(error)}));
      }
    } else {
      try {
        const queryClient = createQueryClient();
        const data = await queryClient.fetchQuery(
          layoutFactory.layout(
            {
              screenUid: selectedSubtab.uid,
            },
            undefined,
            {ssrRequest, cookie}
          )
        );

        if (!data) {
          store.dispatch(
            addError({
              error: thunkSerializeError(new Error('layout 없음')),
            })
          );
        } else {
          layoutId = data.id;
          /**
           * TODO: getServerSideProps -> getInitialProps 로 넘어오면서 기존 hydrate 시 무시되던 page:0 이 getInitialProps 이후로 덮어쓰면서 백버튼을 통해 다시 들어올 시 무조건 0 페이지만 보여주는 현상이 있습니다.
           * react-query 로 변경하면 페이지 정보도 캐싱되기 때문에 자연스럽게 해결될 것으로 보이지만
           * 당장 변경하긴 어려우니 임시로 한번 가져왔던 layout 이면 redux 에 넣지 않도록 합니다.
           */
          if (store.getState().pagewebLayout.entities.layouts[layoutId]) {
            store.dispatch(
              setLayoutIdAndScreenUidMap({
                screenUid: selectedSubtab.uid,
                layoutId,
              })
            );
          } else {
            store.dispatch(
              setLayout({
                layout: data,
                ageGrade: selectedSubtab?.ageGrade,
              })
            );
          }
          return {
            menuUid,
            screenUid,
            metaInfo,
            layoutId,
            queryClient,
          };
        }
      } catch (error) {
        store.dispatch(addError({error: thunkSerializeError(error)}));
      }
    }
  }

  return {
    menuUid,
    screenUid,
    metaInfo,
    layoutId,
  };
}
